import api from './api';

export interface ZonePolygonPoint {
  lat: number;
  lng: number;
}

export interface OperationalZoneRow {
  _id: string;
  code: string;
  name: string;
  zoneLevel: 'PRIMARY' | 'SECONDARY';
  primaryZoneId?: string;
  country: string;
  currency: string;
  timezone: string;
  unit: 'KM' | 'MILE';
  polygon: ZonePolygonPoint[];
  centerLat?: number;
  centerLng?: number;
  color?: string;
  paymentTypes: string[];
  serviceIds: string[] | Array<{ _id: string; name: string; slug: string }>;
  biddingEnabled: boolean;
  nonServiceZone: boolean;
  isActive: boolean;
  metadata?: Record<string, unknown>;
}

export interface ZoneServicePricingRow {
  _id: string;
  serviceId: { _id: string; name: string; slug: string; hubType?: string };
  scheduleType: 'ride_now' | 'ride_later' | 'default';
  baseFare: number;
  baseDistanceKm: number;
  perKmRate: number;
  perMinRate: number;
  perKgRate?: number;
  deliveryCharge?: number;
  minimumFare: number;
}

export interface ZoneDetail {
  zone: OperationalZoneRow;
  servicePricing: ZoneServicePricingRow[];
  surgePricing: Array<{
    _id: string;
    name: string;
    multiplier: number;
    surgePerKm?: number;
    startHour: number;
    endHour: number;
    daysOfWeek: number[];
    serviceId?: { _id: string; name: string } | string;
    metadata?: { startTime?: string; endTime?: string; serviceCategoryCode?: string };
  }>;
}

export async function listZones(params?: { search?: string; page?: number; limit?: number }) {
  const res = await api.get('/admin/zones', { params });
  return { rows: res.data.data as OperationalZoneRow[], meta: res.data.meta };
}

export async function getZone(id: string) {
  const res = await api.get(`/admin/zones/${id}`);
  return res.data.data as ZoneDetail;
}

export async function createZone(payload: Record<string, unknown>) {
  const res = await api.post('/admin/zones', payload);
  return res.data.data as ZoneDetail;
}

export async function updateZone(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/admin/zones/${id}`, payload);
  return res.data.data as ZoneDetail;
}

export async function toggleZone(id: string, isActive: boolean) {
  const res = await api.patch(`/admin/zones/${id}/status`, { isActive });
  return res.data.data;
}

export async function deleteZone(id: string) {
  const res = await api.delete(`/admin/zones/${id}`);
  return res.data.data;
}

export async function listPrimaryZones() {
  const res = await api.get('/admin/zones/primary');
  return res.data.data as Array<{
    _id: string;
    code: string;
    name: string;
    paymentTypes?: string[];
    serviceIds?: Array<{ _id: string; name?: string } | string>;
  }>;
}

export async function listOutOfZoneTiers() {
  const res = await api.get('/admin/out-of-zone-tiers');
  return res.data.data as Array<{ _id: string; kilometer: number; price: number; isActive: boolean }>;
}

export async function createOutOfZoneTier(kilometer: number, price: number) {
  const res = await api.post('/admin/out-of-zone-tiers', { kilometer, price });
  return res.data.data;
}

export async function deleteOutOfZoneTier(id: string) {
  const res = await api.delete(`/admin/out-of-zone-tiers/${id}`);
  return res.data.data;
}

export async function estimateZoneFare(payload: Record<string, unknown>) {
  const res = await api.post('/admin/zones/estimate-fare', payload);
  return res.data.data;
}
